import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Badge from "@insite/mobius/Badge";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomCircleInfoIcon from "@tCentro/Icons/CustomCircleInfoIcon";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showImage: boolean;
    extendedStyles?: NISProductListProductImageStyles;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
    showCompare?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;
    return {
        settingsCollection: getSettingsCollection(state),
        productCompareChecked: getProductCompareChecked(state, props.productContext),
        isProductCompareFull: getIsProductCompareFull(state),
        query: correctedQuery || productFilters.query,
        productListLoading: state.pages.productList.isLoading,
        queryString: state.pages.productList.productFilters.query,
        productsDataView: getProductListDataView(state),
        correctedQuery,
        productFilters,
    };
};

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface NISProductListProductImageStyles {
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    compareCheckbox?: CheckboxPresentationProps;
    addToListWrapper?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    productSaveCompareWrapper?: InjectableCss;
    productImageWrapper?: InjectableCss;
    saveLink?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    topResultStyles?: InjectableCss;
    // infoIconStyles?: IconPresentationProps;
    topResultContainer?: InjectableCss;
}

export const productImageStyles: NISProductListProductImageStyles = {
    topResultContainer: {
        css: css`
            background-color: ${NISCO.COLORS.primaryColor};
            position: absolute;
            display: flex;
            top: 0px;
            left: 0px;
            padding: 4px 8px 4px 8px;
            width: max-content;
            align-items: center;
            gap: 10px;
            border-radius: 4px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        position: relative;
                        top: 0px;
                        left: 0px;
                    `,
                    css`
                        position: relative;
                        top: 0px;
                        left: 0px;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    },
    // infoIconStyles: {
    //     size: 12,
    //     css: css`
    //         > div {
    //             display: flex;
    //         }
    //     `,
    // },
    topResultStyles: {
        css: css`
            font-size: 14px;
            font-weight: 700;
            line-height: 14px;
            text-align: left;
            color: white;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
            text-transform: uppercase;
            position: relative;
            top: 1px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            position: relative;
            width: 100%;
            padding-bottom: 20px;
            a {
                // box-shadow:
                //     0px 0px 1px rgba(38, 37, 37, 0.56),
                //     0px 2px 8px rgba(38, 37, 37, 0.16);
                padding: 16px;
                background: white;
                aspect-ratio: 287/ 287;
                width: 100%;
                height: auto;
                object-fit: contain;
                img {
                    aspect-ratio: 257 / 257;
                    object-fit: contain;
                    max-width:255px;
                    max-height:255px;
                }
            }

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    },
    productImage: {
        image: {
            css: css`
                img {
                    min-width: 100%;
                }
                position: absolute;
                top: 0;
                left: 0;
                z-index: 0;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 75%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    productImageWrapper: {
        css: css`
            background: ${COLORS.white};
            padding: 15px;
            margin-bottom: 15px;
            box-shadow:
                0px 0px 1px rgba(38, 37, 37, 0.56),
                0px 2px 8px rgba(38, 37, 37, 0.16);
            width: 100%;
            height: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 104px;
                    `,
                    css`
                        width: 200px;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
            a {
                justify-content: center;
                height: 100%;
                img {
                    height: auto;
                    width: 100%;
                    object-fit: contain;
                    aspect-ratio: 1/1;
                    max-height: 255px;
                }
            }
        `,
    },
};

const NISProductListProductImage: FC<Props> = ({
    productContext,
    showImage,
    showTextBadge,
    showImageBadge,
    showCompare,
    isProductCompareFull,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
    productsDataView,
    query,
    queryString,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge,
        showImageBadge,
        styles,
    });

    const finalProductObj = productsDataView?.value?.[0];

    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="NISProductListProductImage"
            data-test-key={productContext.product.id}
        >
            {showImage && !showOverlayBadge && (
                <>
                    {query && productContext.product.id === finalProductObj?.id && (
                        <StyledWrapper {...styles.topResultContainer}>
                            <Typography as="span" {...styles.topResultStyles}>
                                {translate("Top Result")}
                            </Typography>
                            {/* <Icon src={CustomCircleInfoIcon} {...styles.infoIconStyles} /> */}
                        </StyledWrapper>
                    )}
                    <ProductImage extendedStyles={styles.productImage} product={productContext} />
                </>
            )}
            {showImage && showOverlayBadge && overlayBadge && (
                <StyledWrapper
                    {...styles.badgeAreaWrapper}
                    ref={badgeAreaWrapperRef}
                    data-test-selector="productListBadge"
                >
                    <ProductImage extendedStyles={styles.productImage} product={productContext} />
                    <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                        <Badge
                            type={overlayBadge.badgeType}
                            value={
                                overlayBadge.badgeType === "Image"
                                    ? overlayBadge.largeImageBadgePath
                                    : overlayBadge.displayText
                            }
                            badgeTextColor={overlayBadge.textColorHexCode}
                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                            imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                            textBadgeCss={styles.badgeText}
                            imageBadgeProps={styles.badgeImageProps}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(NISProductListProductImage));
